// App
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// Components
import AllFiltersButton from 'app/shared/modules/filter/FilterBar/AllFiltersButton';
import PriceFilterButton from 'app/shared/modules/filter/FilterBar/PriceFilterButton';
import BedBathFilterButton from 'app/shared/modules/filter/FilterBar/BedBathFilterButton';
import CommuteFilterButton from 'app/shared/modules/filter/FilterBar/CommuteFilterButton';
import PetFilterButton from 'app/shared/modules/filter/FilterBar/PetFilterButton';
import ClearAllFiltersButton from 'app/shared/modules/filter/FilterBar/ClearAllFiltersButton';

// Hooks
import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';

// Misc / utils
import { filterUtils_hasActiveFilters } from 'app/shared/utils/filterUtils';
import * as S from './styles';
import type { RootReduxState } from 'app/shared/flux/reducers';

const INIT_STATE = {
  allFilters: false,
  priceFilter: false,
  bedBathFilter: false,
  commuteFilter: false,
  petsFilter: false,
};
const FilterBar = () => {
  // Redux
  const filter = useSelector((state: RootReduxState) => state.filter);

  // Captures filter state when a dropdown opens so shouldFetchFilteredResults can
  // distinguish slug-implied propertyTypes from user changes (same pattern as
  // FilterModalController). Without this, closing a dropdown on a property-type page
  // (e.g. houses-for-rent) triggers an unnecessary fetch.
  const [filterAtDropdownOpen, setFilterAtDropdownOpen] = useState<typeof filter | null>(null);

  // Hooks
  const { filterChange, shouldFetchFilteredResults } = useFilterChange(filterAtDropdownOpen);

  // React state
  const [showDropdown, setShowDropdown] = useState(INIT_STATE);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // Generation counter to cancel stale deferred toggles when Done/close fires
  const toggleGenRef = useRef(0);

  const isAnyDropdownOpen = Object.values(showDropdown).some(Boolean);

  useEffect(() => {
    if (isAnyDropdownOpen) {
      setFilterAtDropdownOpen({ ...filter });
    }
    // filter intentionally excluded: we only want the value at the moment a dropdown opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnyDropdownOpen]);

  useEffect(() => {
    setHasActiveFilters(filterUtils_hasActiveFilters(filter));
  }, [filter]);

  const yieldShowDropdown = useCallback((option = {}) => {
    const expectedGen = ++toggleGenRef.current;
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (toggleGenRef.current !== expectedGen) return;
        setShowDropdown({ ...INIT_STATE, ...option });
      }, 0);
    });
  }, []);

  const handleAllFiltersButtonClick = () => {
    yieldShowDropdown({ allFilters: !showDropdown.allFilters });
  };
  const handlePriceFilterButtonClick = () => {
    yieldShowDropdown({ priceFilter: !showDropdown.priceFilter });
  };
  const handleBedBathButtonClick = () => yieldShowDropdown({ bedBathFilter: !showDropdown.bedBathFilter });
  const handleCommuteButtonClick = () => yieldShowDropdown({ commuteFilter: !showDropdown.commuteFilter });
  const handlePetButtonClick = () => yieldShowDropdown({ petsFilter: !showDropdown.petsFilter });

  const handleClose = () => {
    toggleGenRef.current++;
    setShowDropdown(INIT_STATE);
    if (shouldFetchFilteredResults()) {
      setTimeout(() => filterChange({ ...filter }), 0);
    }
  };

  return (
    <>
      <S.FilterBarSection>
        <AllFiltersButton
          ariaButtonLabel={'Show all filters'}
          ariaDropdownLabel={'List of all filters available to fine tune your rental search'}
          onButtonClick={handleAllFiltersButtonClick}
          onModalClose={handleClose}
          showDropdown={showDropdown.allFilters}
        />
        <PriceFilterButton
          ariaButtonLabel={'Show price filter'}
          ariaDropdownLabel={'Set your minimum and maximum price preferences and listings will be updated accordingly'}
          onButtonClick={handlePriceFilterButtonClick}
          onModalClose={handleClose}
          showDropdown={showDropdown.priceFilter}
        />
        <BedBathFilterButton
          ariaButtonLabel={'Show bedroom and bathroom filters'}
          ariaDropdownLabel={'Set your bedroom and bathroom preferences and listings will be updated accordingly'}
          onButtonClick={handleBedBathButtonClick}
          onModalClose={handleClose}
          showDropdown={showDropdown.bedBathFilter}
        />
        <CommuteFilterButton
          ariaButtonLabel={'Show daily commute filters'}
          ariaDropdownLabel={'Set your daily commute preferences and listings will be updated accordingly'}
          onButtonClick={handleCommuteButtonClick}
          onModalClose={handleClose}
          showDropdown={showDropdown.commuteFilter}
        />
        <PetFilterButton
          ariaButtonLabel={'Show pet friendly filters'}
          ariaDropdownLabel={'Set your pet filter preferences and listings will be updated accordingly'}
          onButtonClick={handlePetButtonClick}
          onModalClose={handleClose}
          showDropdown={showDropdown.petsFilter}
        />
        {hasActiveFilters && <ClearAllFiltersButton />}
      </S.FilterBarSection>
    </>
  );
};

export default FilterBar;
